import { useState, useEffect } from 'react';
import Drawings from './Drawings';
import Paintings from './Paintings';
import CV from './CV';
import Home from './Home';
import Research from './Research';
import AvailableWork from './AvailableWork';
import { prefetchGalleries } from './galleryCache';

function Menu({ navigate, currentPath, isOpen, setIsOpen }) {
  const handleClick = (path) => (e) => {
    e.preventDefault();
    navigate(path);
    setIsOpen(false);
  };

  const linkClass = (path) =>
    currentPath === path
      ? "py-2 text-gray-400 pointer-events-none"
      : "py-2 hover:text-gray-500";

  return (
    <>
      {/* Desktop menu */}
      <nav className="hidden md:flex fixed top-20 left-8 z-50 flex-col text-2xl">
        <a href="/research" onClick={handleClick('/research')} className={linkClass('/research')}>Research</a>
        <a href="/paintings" onClick={handleClick('/paintings')} className={linkClass('/paintings')}>Paintings</a>
        <a href="/drawings" onClick={handleClick('/drawings')} className={linkClass('/drawings')}>Drawings</a>
        <a href="/cv" onClick={handleClick('/cv')} className={linkClass('/cv')}>CV</a>
      </nav>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 bg-white z-40 pt-20">
          <nav className="flex flex-col items-start text-2xl mt-8 px-4">
            <a href="/research" onClick={handleClick('/research')} className={linkClass('/research')}>Research</a>
            <a href="/paintings" onClick={handleClick('/paintings')} className={linkClass('/paintings')}>Paintings</a>
            <a href="/drawings" onClick={handleClick('/drawings')} className={linkClass('/drawings')}>Drawings</a>
            <a href="/cv" onClick={handleClick('/cv')} className={linkClass('/cv')}>CV</a>
          </nav>
        </div>
      )}
    </>
  );
}

function Footer({ navigate }) {
  const handleClick = (e) => {
    e.preventDefault();
    navigate('/');
  };

  return (
    <footer className="fixed bottom-0 left-0 right-0 z-50 bg-white py-4 md:py-8 px-4 md:px-8">
      <a href="/" onClick={handleClick} className="text-xl md:text-2xl hover:text-gray-500">
        &#8249; Back
      </a>
    </footer>
  );
}

function Header({ isMenuOpen, setIsMenuOpen, navigate }) {
  const handleClick = (e) => {
    e.preventDefault();
    navigate('/');
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 pt-4 md:pt-8 pb-4 md:pb-6 bg-white px-4 md:px-8">
      <div className="flex items-center justify-between md:justify-end">
        {/* Hamburger button - mobile only */}
        <button
          className="md:hidden p-2 -ml-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-6 flex flex-col gap-1.5">
            <span className={`block h-0.5 bg-black transition-transform ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block h-0.5 bg-black transition-opacity ${isMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 bg-black transition-transform ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </div>
        </button>
        <a href="/" onClick={handleClick} className="text-2xl md:text-4xl font-semibold hover:text-gray-600">
          Mariya Vodyanyk, PhD
        </a>
      </div>
    </div>
  );
}

function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigate = (newPath) => {
    window.history.pushState({}, '', newPath);
    setPath(newPath);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    prefetchGalleries();
  }, []);

  return (
    <div className="min-h-screen w-full flex flex-col bg-white">
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} navigate={navigate} />
      <Menu navigate={navigate} currentPath={path} isOpen={isMenuOpen} setIsOpen={setIsMenuOpen} />
      <div className="flex-1">
        {path === '/' && <Home />}
        {path === '/research' && <Research />}
        {path === '/paintings' && <Paintings />}
        {path === '/drawings' && <Drawings />}
        {path === '/cv' && <CV />}
        {path === '/available-work' && <AvailableWork />}
      </div>
      {path !== '/' && <Footer navigate={navigate} />}
    </div>
  );
}

export default App;
